import { Flex, FormControl, Switch, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  type CustomContractDataFieldDto,
  useMarkSupplierFilterContractEndDateMutation,
  useMarkSupplierFilterContractFieldMutation,
} from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../../common/errors/useApiError";
import { useAppDispatch } from "../../../../common/redux/hooks";
import { setSupplierFilterField, setSupplierFilterOnEndDate } from "../../../../common/redux/reducers/authStateReducer";

const FILTERABLE_TYPES: CustomContractDataFieldDto["type"][] = [
  "CustomEnum",
  "CustomMultiEnum",
  "CustomBoolean",
  "CustomNumber",
  "CustomAmount",
  "CustomPercentage",
  "CustomDate",
];

export const canFilterSuppliers = (field: CustomContractDataFieldDto): boolean => FILTERABLE_TYPES.includes(field.type);

export const SupplierFilterToggle = ({ field }: { field: CustomContractDataFieldDto }) => {
  const dispatch = useAppDispatch();
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const [mark, { isLoading }] = useMarkSupplierFilterContractFieldMutation();
  const marked = (selectedOrg.supplierFilterFieldIds ?? []).includes(field.id);

  if (!canFilterSuppliers(field)) return null;

  return (
    <SupplierFilterSwitch
      marked={marked}
      isLoading={isLoading}
      onChange={async (next) => {
        dispatch(setSupplierFilterField({ fieldId: field.id, marked: next }));
        try {
          await mark({
            orgId: selectedOrg.id,
            markSupplierFilterFieldDto: { fieldId: field.id, marked: next },
          }).unwrap();
        } catch (e) {
          dispatch(setSupplierFilterField({ fieldId: field.id, marked: !next }));
          throw e;
        }
      }}
    />
  );
};

export const SupplierFilterEndDateToggle = () => {
  const dispatch = useAppDispatch();
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const [mark, { isLoading }] = useMarkSupplierFilterContractEndDateMutation();

  return (
    <SupplierFilterSwitch
      marked={selectedOrg.supplierFilterOnEndDate ?? false}
      isLoading={isLoading}
      onChange={async (next) => {
        dispatch(setSupplierFilterOnEndDate(next));
        try {
          await mark({ orgId: selectedOrg.id, markSupplierFilterEndDateDto: { marked: next } }).unwrap();
        } catch (e) {
          dispatch(setSupplierFilterOnEndDate(!next));
          throw e;
        }
      }}
    />
  );
};

const SupplierFilterSwitch = ({
  marked,
  isLoading,
  onChange,
}: {
  marked: boolean;
  isLoading: boolean;
  onChange: (next: boolean) => Promise<void>;
}) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();

  return (
    <FormControl width="96" alignItems="center" py="2">
      <Flex alignItems="center">
        <Switch
          rounded="md"
          mr="2"
          size="sm"
          aria-label={t("Use as a supplier filter") ?? undefined}
          isChecked={marked}
          isDisabled={isLoading}
          onChange={(e) => void onChange(e.target.checked).catch(trigger)}
        />
        <Text fontSize="xs" color="smMuted">
          {marked
            ? t("Supplier filter - buyers can filter suppliers in the Invitations step on this field")
            : t("Not a supplier filter - not offered in the Invitations step")}
        </Text>
      </Flex>
    </FormControl>
  );
};
